import {
  ComponentProps,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

import { setCookie } from 'nookies'

import { Loader } from 'components'

import { useGetTenantByCode } from 'services/tenant'
import { TenantData, TenantDomain } from 'services/tenant/types'

import { useTheme } from '../useTheme'
import { handleBrandColors, handleFavicon } from 'shared/hooks/useTenant/utils'

interface TenantContextProps {
  tenant: TenantDomain
  setTenant: (tenant: TenantData) => void
}

const TenantContext = createContext<TenantContextProps | null>(null)

const hostname = window.location.hostname
const isLocalEnvironment = hostname.includes('localhost')

export const TenantProvider = ({
  children,
  ...props
}: ComponentProps<'div'>) => {
  const { theme } = useTheme()

  const hotsname =
    hostname.split('.')[0] === 'portal' ? 'orsegups' : hostname.split('.')[0]

  const tenantCode = isLocalEnvironment ? process.env.TENANT_CODE : hotsname

  const hasTenantDefined =
    !!tenantCode && !['app', 'localhost'].includes(tenantCode)

  const [tenant, setTenant] = useState<TenantData | undefined>(undefined)

  const { data, isLoading } = useGetTenantByCode(
    hasTenantDefined ? tenantCode : '',
  )

  const handleSetHostname = (code: string) => {
    if (!isLocalEnvironment) {
      window.location.href = window.location.href.replace(
        window.location.hostname.split('.')[0],
        code,
      )
    }
  }

  const handleSetTenantCookies = (tenant: TenantData) => {
    setCookie(undefined, 'tenantId', tenant.id, {
      maxAge: 600 / (60 * 24),
    })

    setCookie(undefined, 'tenantCode', tenant.code, {
      maxAge: 600 / (60 * 24),
    })
  }

  const handleSetTenant = (tenant: TenantData) => {
    setTenant(tenant)

    handleSetTenantCookies(tenant)
    handleSetHostname(tenant.code)
  }

  useEffect(() => {
    if (!tenant && data) {
      setTenant(data)
      handleSetTenantCookies(data)

      sessionStorage.setItem('tenantId', data?.id || '')
      sessionStorage.setItem('tenantCode', data.code)

      document.documentElement.setAttribute('tenant', data.code)

      const { lightTheme, darkTheme } = data.brand

      handleBrandColors({ light: lightTheme.color, dark: darkTheme.color })

      handleFavicon(
        theme === 'dark' ? darkTheme.images.logo : lightTheme.images.logo,
      )
    }
  }, [data])

  return (
    <TenantContext.Provider
      value={{
        ...(tenant && {
          tenant: {
            ...tenant,
            brand:
              tenant?.brand?.[theme === 'dark' ? 'darkTheme' : 'lightTheme'],
          },
        }),
        setTenant: handleSetTenant,
      }}
      {...props}
    >
      {isLoading ? (
        <Loader appearance="screen" style={{ height: '100vh' }} />
      ) : (
        children
      )}
    </TenantContext.Provider>
  )
}

export const useTenant = () => {
  const context = useContext(TenantContext)

  if (!context) throw new Error('useTenant must be used within TenantProvider')

  return context
}
